import { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { FaChartLine, FaHistory } from 'react-icons/fa';
import productivityForecastApi from '../../../services/productivityForecastApi';
import productionRecordsApi from '../../../services/productionRecordsApi';
import monitoringApi from '../../../services/monitoringApi';
import { ChartCard, lineOptions } from '../analytics/analyticsShared';

const DIRECTION_STYLES = {
  improving: 'bg-green-50 text-green-700 ring-green-200',
  stable: 'bg-gray-50 text-gray-600 ring-gray-200',
  worsening: 'bg-red-50 text-red-700 ring-red-200',
};

// A REAL forecast: a linear trend fitted to this device's own logged
// Production Records, projected 7 days forward. The chart plots actual
// historical points plus one projected point — nothing in between is
// invented. If there isn't enough data, this says so instead of guessing.
export default function AiProductivityForecast() {
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [records, setRecords] = useState([]);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await monitoringApi.listDevices();
        setDevices(res.devices || []);
        if (res.devices?.length) setSelectedDeviceId(res.devices[0]._id);
      } catch (err) {
        /* interceptor toasts */
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedDeviceId) return;
    (async () => {
      try {
        const [recRes, histRes] = await Promise.all([
          productionRecordsApi.getForDevice(selectedDeviceId),
          productivityForecastApi.getForDevice(selectedDeviceId),
        ]);
        setRecords(recRes.records || []);
        setHistory(histRes.reports || []);
        setReport(null);
      } catch (err) {
        /* interceptor toasts */
      }
    })();
  }, [selectedDeviceId]);

  const handleGenerate = async () => {
    if (!selectedDeviceId) return;
    setGenerating(true);
    setError('');
    try {
      const res = await productivityForecastApi.generate(selectedDeviceId);
      setReport(res.report);
      const histRes = await productivityForecastApi.getForDevice(selectedDeviceId);
      setHistory(histRes.reports || []);
    } catch (err) {
      setError(typeof err === 'string' ? err : err?.error || 'Could not generate forecast.');
    } finally {
      setGenerating(false);
    }
  };

  const chartData = useMemo(() => {
    if (!records.length) return null;
    const labels = records.map((r) => new Date(r.recordDate).toLocaleDateString());
    const mortalitySeries = records.map((r) => r.mortalityRatePercent);

    if (report?.hasEnoughData && report.projectedMortalityRatePercent7d != null) {
      labels.push('+7d (projected)');
      mortalitySeries.push(null); // break the solid line
    }

    const datasets = [
      {
        label: 'Mortality rate % (recorded)',
        data: mortalitySeries,
        borderColor: '#1e5c33',
        backgroundColor: 'rgba(30,92,51,0.08)',
        tension: 0.25,
        spanGaps: false,
      },
    ];

    if (report?.hasEnoughData && report.projectedMortalityRatePercent7d != null) {
      const projectedSeries = records.map(() => null);
      projectedSeries[records.length - 1] = records[records.length - 1].mortalityRatePercent;
      projectedSeries.push(report.projectedMortalityRatePercent7d);
      datasets.push({
        label: 'Projected (+7d)',
        data: projectedSeries,
        borderColor: '#D98E04',
        borderDash: [6, 4],
        backgroundColor: 'transparent',
        tension: 0,
        spanGaps: true,
      });
    }

    return { labels, datasets };
  }, [records, report]);

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FaChartLine className="text-primary" /> AI Productivity Forecast
          </h1>
          <p className="text-xs text-gray-400 mt-1">
            A real trend fitted to this house's own Production Records, projected 7 days forward.
          </p>
        </div>
        <button
          onClick={() => setShowHistory((s) => !s)}
          className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
        >
          <FaHistory /> {showHistory ? 'Hide' : 'View'} history ({history.length})
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-wrap items-center gap-3">
        <label className="text-xs text-gray-500">House / device:</label>
        <select
          value={selectedDeviceId}
          onChange={(e) => setSelectedDeviceId(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        >
          {devices.map((d) => (
            <option key={d._id} value={d._id}>{d.houseName || d.name}</option>
          ))}
        </select>
        <button
          onClick={handleGenerate}
          disabled={generating || !selectedDeviceId}
          className="px-5 py-2.5 rounded-lg bg-primary text-white text-sm disabled:opacity-60"
        >
          {generating ? 'Analysing trend…' : 'Generate forecast'}
        </button>
        {error && <p className="text-sm text-red-600 w-full">{error}</p>}
      </div>

      {showHistory && history.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-2 max-h-56 overflow-y-auto">
          {history.map((r) => (
            <div key={r._id} className="text-sm border-b border-gray-50 pb-2 flex justify-between">
              <span className="text-gray-500">{new Date(r.createdAt).toLocaleString()}</span>
              {r.hasEnoughData ? (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${DIRECTION_STYLES[r.mortalityDirection] || DIRECTION_STYLES.stable}`}>
                  {r.mortalityDirection}
                </span>
              ) : (
                <span className="text-xs text-gray-400">insufficient data</span>
              )}
            </div>
          ))}
        </div>
      )}

      {chartData && (
        <ChartCard title="Mortality rate trend + 7-day projection">
          <div className="h-64">
            <Line data={chartData} options={lineOptions} />
          </div>
        </ChartCard>
      )}

      {records.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-sm text-gray-400">
          No production records logged for this house yet. Log at least 2 records (Production Records page) before generating a forecast.
        </div>
      )}

      {report && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
          {!report.hasEnoughData ? (
            <p className="text-gray-600">{report.summary}</p>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ring-1 ring-inset ${DIRECTION_STYLES[report.mortalityDirection]}`}>
                  Mortality trend: {report.mortalityDirection}
                </span>
                <span className="text-xs text-gray-400">based on {report.recordCount} real records</span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-400">Current mortality rate</div>
                  <div className="text-lg font-semibold text-gray-800">{report.currentMortalityRatePercent}%</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-400">Projected in 7 days</div>
                  <div className="text-lg font-semibold text-gray-800">
                    {report.projectedMortalityRatePercent7d != null ? `${report.projectedMortalityRatePercent7d}%` : 'Not computable'}
                  </div>
                </div>
              </div>

              <p className="text-gray-700">{report.summary}</p>

              {report.recommendations?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-1">Recommendations</h3>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-0.5">
                    {report.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}
          <p className="text-xs text-gray-400 italic">{report.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
